import React from "react";
import { CostMetadata } from "../../../../../../common/models";
import { ColumnConfig } from "../../../../common/tables/config/ColumnConfig";
import { EnumResolver } from "../../../../common/tables/etc/EnumResolver";
import {
    TableCellChangedHandler,
    TableCellFactory,
} from "../../../../common/tables/factory";
import { CostMetadataTableRow } from "../../../../../../common/tables/cost-metadata/CostMetadataTableRow";
import { CostMetadataEnumMapper } from "../etc/CostMetadataEnumMapper";

export interface CostMetadataTableCellCallbacks {
    onVariantsCellClick?: (rowData: CostMetadata) => void;
    onModifiersCellClick?: (rowData: CostMetadata) => void;
}

export class CostMetadataTableCellFactory extends TableCellFactory<CostMetadataTableRow, CostMetadata> {
    readonly enumResolver: EnumResolver<CostMetadataTableRow> = new CostMetadataEnumMapper();
    private callbacks: CostMetadataTableCellCallbacks;

    constructor(callbacks: CostMetadataTableCellCallbacks = {}) {
        super();
        this.callbacks = callbacks;
    }

    public renderBodyCell(
        columnConfig: ColumnConfig<CostMetadataTableRow>,
        rowData: CostMetadata,
        onCellChanged: TableCellChangedHandler<CostMetadataTableRow>,
    ): JSX.Element {
        if (columnConfig.columnName === "variantSkus") {
            const cellValue = this.resolveCellData(columnConfig, rowData);
            return React.createElement(
                "span",
                {
                    className: "jc-cell-clickable",
                    onClick: () => this.callbacks.onVariantsCellClick?.(rowData),
                },
                cellValue || "\u2014"
            );
        }

        if (columnConfig.columnName === "costPayoutModifierName") {
            const cellValue = this.resolveCellData(columnConfig, rowData);
            if (!cellValue) {
                return super.renderBodyCell(columnConfig, rowData, onCellChanged);
            }
            return React.createElement(
                "span",
                {
                    className: "jc-cell-clickable",
                    onClick: () => this.callbacks.onModifiersCellClick?.(rowData),
                },
                cellValue
            );
        }

        return super.renderBodyCell(columnConfig, rowData, onCellChanged);
    }

    protected resolveCellData(
        columnConfig: ColumnConfig<CostMetadataTableRow>,
        rowData: CostMetadata,
    ): string {
        const data = rowData as any;

        if (columnConfig.columnName === "variantSkus") {
            const variants = data.shopifyProductVariant ?? [];
            return variants.map((v: any) => v.sku || v.title || v.id).join(", ");
        }

        if (columnConfig.columnName === "costPayoutModifierName") {
            return data.costPayoutModifier?.name ?? "";
        }

        return super.resolveCellData(columnConfig, rowData);
    }
}
